/**
 * Choosing which delivery request is "the" delivery for an order.
 *
 * An order can have several requests: a seller who cancels a transport
 * booking and asks again leaves the cancelled request behind, next to the
 * new one, under the same order. Code that takes the first or last row back
 * can land on the dead booking instead of the live one.
 *
 * Mirrors backend/utils/delivery_requests.py::pick_current_request exactly.
 */

// Both spellings are live in the data: the seller app writes "canceled",
// older rows say "cancelled".
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);

export function isCancelled(row) {
  return CANCELLED_STATUSES.has(String(row?.status || '').trim().toLowerCase());
}

/**
 * The request that represents this delivery now: the newest one that is
 * not cancelled. Only if every request was cancelled is the newest
 * cancelled one returned, so callers still see an attempt was made rather
 * than nothing at all.
 */
export function pickCurrentRequest(rows) {
  if (!rows || rows.length === 0) return null;

  const newestFirst = [...rows].sort((a, b) => {
    const aTime = String(a?.$createdAt || a?.createdAt || '');
    const bTime = String(b?.$createdAt || b?.createdAt || '');
    return bTime.localeCompare(aTime);
  });

  return newestFirst.find((row) => !isCancelled(row)) || newestFirst[0];
}

/** Groups delivery-request rows by orderId, keeping only the current one per order. */
export function currentRequestByOrderId(rows) {
  const byOrder = {};
  (rows || []).forEach((r) => {
    if (!r.orderId) return;
    if (!byOrder[r.orderId]) byOrder[r.orderId] = [];
    byOrder[r.orderId].push(r);
  });

  const current = {};
  Object.keys(byOrder).forEach((orderId) => {
    current[orderId] = pickCurrentRequest(byOrder[orderId]);
  });
  return current;
}
